import React, { useEffect, useState } from 'react'
import CommonTable from '../../Components/CommonTable'
import CommonModal from '../../Components/CommonModal'
import ScreenLoader from '../../Components/ScreenLoader'
import RaiseExceptionModal from './RaiseExceptionModal'
import { create_takedown_request_from_finding } from '../../services'

// Categories where a "Confirm Takedown" action makes sense — i.e. where a
// finding has a takedown-able target (a third-party domain / social handle
// / lookalike). NMT-PS operates the takedown after customer admin confirms;
// SLA is ≤24h initiate + ≤5d resolve, tracked in TakedownSlaWidget.
const TAKEDOWN_CATEGORIES = new Set([
    'Brand Risk Monitoring',
    'Application Security',
    'Network Security',
    'Public Exposure',
]);


function IssueCountsModal(props) {
    const [evidenceData, setEvidenceData] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [showRaiseExceptionModal, setShowRaiseExceptionModal] = useState(false)
    const [selectedEvidenceTimelineId, setSelectedEvidenceTimelineId] = useState(null)
    // Keyed by `__rowKey` (which row is ticked), NOT by timeline id — see the
    // note where rows are built. `selectedTimelineIds` below converts back to
    // real ids for the API.
    const [selectedKeys, setSelectedKeys] = useState([])
    const [expandedKeys, setExpandedKeys] = useState(new Set())

    // Takedown confirmation state
    const [showTakedownConfirm, setShowTakedownConfirm] = useState(false)
    const [takedownBusy, setTakedownBusy] = useState(false)
    const [takedownResult, setTakedownResult] = useState(null)   // {ok, message}

    const findingId = props?.evidencelist?.id
        || props?.evidencelist?.finding_timeline_id
        || null
    const category = props?.evidencelist?.category || ''
    // `allowTakedown` defaults to true so EASM is unchanged. The TPCRM vendor
    // profile passes false: those findings belong to the third party, and
    // offering to take down their asset from their own profile is wrong.
    const allowTakedown = props.allowTakedown !== false
    const canTakedown = allowTakedown && !!findingId && TAKEDOWN_CATEGORIES.has(category)

    // Derive the target domain to display in the confirmation modal
    const targetDomain = (() => {
        const url = props?.evidencelist?.source_url || ''
        if (url) {
            try {
                return new URL(url.startsWith('http') ? url : 'https://' + url).host
            } catch (_) { /* fall through */ }
        }
        return props?.evidencelist?.domain || ''
    })()

    const isDWI = props?.evidencelist?.category === 'Dark Web Intelligence'

    async function handleConfirmTakedown() {
        if (!findingId || takedownBusy) return
        setTakedownBusy(true)
        setTakedownResult(null)
        try {
            const resp = await create_takedown_request_from_finding(findingId, {})
            const body = resp?.statusCode?.response_data?.data
                ?? resp?.statusCode ?? resp?.data ?? resp ?? {}
            if (body?.takedown_request_id) {
                setTakedownResult({
                    ok: true,
                    message: `Takedown request #${body.takedown_request_id} submitted. NMT will initiate within 24 hours and target resolution within 5 days.`,
                    sla_initiate: body.sla_initiate_target,
                    sla_resolve: body.sla_resolve_target,
                })
                // Refresh the parent (so the SLA widget re-appears).
                props.onRefresh?.()
            } else {
                setTakedownResult({
                    ok: false,
                    message: body?.error || 'Takedown submission failed.',
                })
            }
        } catch (e) {
            setTakedownResult({ ok: false, message: e?.message || 'Network error' })
        } finally {
            setTakedownBusy(false)
        }
    }

    useEffect(() => {
        async function loadData() {
            setIsLoading(true)
            try {
                let list = props?.evidencelist?.evidence_list

                // No list? clear & exit
                if (!list) {
                    setEvidenceData([])
                    return
                }

                // Parse JSON‑string if needed
                if (typeof list === 'string') {
                    try { list = JSON.parse(list) }
                    catch { console.error('Invalid JSON in evidence_list'); list = [] }
                }

                // Single object → array
                if (!Array.isArray(list) && typeof list === 'object') {
                    list = [list]
                }

                const scanDate = props.evidencelist.last_scan_date
                const rows = []

                list.forEach(item => {
                    const { evidence, ...rest } = item

                    // Normalize evidence into an array
                    let ev = evidence
                    if (typeof ev === 'string') {
                        try { ev = JSON.parse(ev) }
                        catch { ev = [] }
                    }
                    if (ev && !Array.isArray(ev) && typeof ev === 'object') {
                        ev = [ev]
                    }

                    // Flatten each evidence entry
                    ; (Array.isArray(ev) ? ev : []).forEach(e => {
                        rows.push({
                            ...rest,
                            last_scan_date: scanDate,
                            ...e,
                            // Each evidence entry becomes its own row, but they
                            // are all spread from the SAME parent (`rest`), so
                            // any id on the parent is identical across them —
                            // and an evidence entry usually carries no id of its
                            // own. Checkbox state keyed on that id therefore
                            // toggled every row at once. `__rowKey` gives each
                            // row an identity of its own, independent of
                            // whatever the payload does or doesn't provide.
                            __rowKey: `row-${rows.length}`,
                        })
                    })
                })

                setEvidenceData(rows)
            } finally {
                setIsLoading(false)
            }
        }

        loadData()
    }, [props.evidencelist])

    // Build dynamic headers
    const allKeys = Array.from(
        new Set(evidenceData.flatMap(r => Object.keys(r)))
    )

    // Force last_scan_date to the end and exclude exception-related fields
    const excludedKeys = [
        '__rowKey',
        'last_scan_date',
        'evidence_timeline_id',
        'has_exception',
        'exception_status',
        'exception_id',
        'exception_details'
    ];
    // Force suites_name to the start and filter out excluded fields
    const orderedKeys = [
        ...(allKeys.includes('suites_name') ? ['suites_name'] : []),
        ...allKeys.filter(k => k !== 'suites_name' && !excludedKeys.includes(k)),
    ]

    // Sort data by suites_name for proper grouping
    const sortedEvidenceData = [...evidenceData].sort((a, b) => {
        const aName = a.suites_name || ''
        const bName = b.suites_name || ''
        return aName.localeCompare(bName)
    })

    const getTimelineId = (record) => record?.id || record?.evidence_timeline_id || record?.timeline_id;
    const getRowKey = (record) => record?.__rowKey;

    // A row is selectable only if it isn't already pending AND it has a real
    // timeline id — selection exists to raise an exception against something,
    // and a row the API can't reference has nothing to raise.
    const isSelectable = (r) => r?.exception_status !== 'PENDING' && Boolean(getTimelineId(r));
    const selectableKeys = sortedEvidenceData.filter(isSelectable).map(getRowKey);

    const isAllSelected = selectableKeys.length > 0
        && selectableKeys.every(key => selectedKeys.includes(key));

    const handleSelectAll = () => {
        setSelectedKeys(isAllSelected ? [] : [...selectableKeys]);
    };

    const handleSelectRow = (rowKey) => {
        setSelectedKeys(prev =>
            prev.includes(rowKey)
                ? prev.filter(key => key !== rowKey)
                : [...prev, rowKey]
        );
    };

    // Back to real ids for the API. De-duplicated, because several rows can
    // legitimately share one timeline id.
    const selectedTimelineIds = [...new Set(
        sortedEvidenceData
            .filter(r => selectedKeys.includes(getRowKey(r)))
            .map(getTimelineId)
            .filter(Boolean)
    )];

    const headers = [
        ...(isDWI ? [
            <input
                type="checkbox"
                checked={isAllSelected}
                onChange={handleSelectAll}
                // Nothing selectable → disabled, rather than a checkbox that
                // looks live and silently does nothing.
                disabled={selectableKeys.length === 0}
                title={selectableKeys.length === 0 ? 'Nothing selectable' : 'Select all'}
                style={{
                    cursor: selectableKeys.length === 0 ? 'not-allowed' : 'pointer',
                    width: 16,
                    height: 16,
                }}
            />
        ] : []),
        ...orderedKeys.map(key =>
            key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, c => c.toUpperCase())
        ),
        ...(isDWI ? [] : ["Raise Exception"])
    ]

    // Calculate rowSpan for suites_name grouping
    const suitesNameIndex = orderedKeys.indexOf('suites_name')
    const suitesNameGroups = {}

    if (suitesNameIndex !== -1) {
        sortedEvidenceData.forEach((record, index) => {
            const suiteName = record.suites_name || '-'
            if (!suitesNameGroups[suiteName]) {
                suitesNameGroups[suiteName] = { startIndex: index, count: 1 }
            } else {
                suitesNameGroups[suiteName].count++
            }
        })
    }

    // Toggle function for expanding/collapsing text
    const toggleExpand = (rowIndex, key) => {
        const keyId = `${rowIndex}-${key}`;
        setExpandedKeys(prev => {
            const newSet = new Set(prev);
            if (newSet.has(keyId)) {
                newSet.delete(keyId);
            } else {
                newSet.add(keyId);
            }
            return newSet;
        });
    };

    // Helper function to get cell value with formatting
    const getCellValue = (key, v, rowIndex) => {
        // Last scan date: format YYYY-MM-DD
        if (key === 'first_observed' || key === 'last_observed') {
            if (typeof v === 'string' && !isNaN(Date.parse(v))) {
                const d = new Date(v);
                const yyyy = d.getFullYear();
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const dd = String(d.getDate()).padStart(2, '0');
                return <span className="last-scan-date-width">{`${yyyy}-${mm}-${dd}`}</span>;
            }
            return <span className="last-scan-date-width">{v ?? '-'}</span>;
        }

        if (key === 'password') {
            return (
                <span className="three_dots_text">
                    {v != null ? String(v) : '-'}
                </span>
            )
        }
        if (key === 'sample_directories') {
            return (
                <span className="three_dots_text">
                    {v != null ? String(v) : '-'}
                </span>
            )
        }
        if (key === 'source') {
            return (
                <span className="three_dots_text">
                    {v != null ? String(v) : '-'}
                </span>
            )
        }

        if (key === 'dns_text') {
            return (
                <span className="three_dots_text">
                    {v != null ? String(v) : '-'}
                </span>
            )
        }

        if (key === 'urls') {
            return (
                <span className="three_dots_text">
                    {v != null ? String(v) : '-'}
                </span>
            )
        }
        if (key === 'exposed_key_preview') {
            const keyId = `${rowIndex}-${key}`;
            const isExpanded = expandedKeys.has(keyId);
            const textValue = v != null ? String(v) : '-';
            const shouldShowButton = textValue !== '-';

            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span className={isExpanded ? "" : "three_dots_text with_3_lines"}>
                        {textValue}
                    </span>
                    {shouldShowButton && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(rowIndex, key);
                            }}
                            className="blue_text cursor_pointer"
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: '0',
                                fontSize: '14px',
                                textAlign: 'left',
                                marginTop: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            {isExpanded ? 'View Less' : 'View More'}
                        </button>
                    )}
                </div>
            )
        }
        if (key === 'commit_url') {
            return (
                <span className="three_dots_text">
                    <a className="obs_link" href={v != null ? String(v) : '-'} target="_blank" rel="noopener noreferrer">{v != null ? String(v) : '-'}</a>
                </span>
            )
        }
        if (key === 'repository') {
            return (
                <span className="three_dots_text">
                    <a className="obs_link" href={v != null ? String(v) : '-'} target="_blank" rel="noopener noreferrer">{v != null ? String(v) : '-'}</a>
                </span>
            )
        }

        // Generic URL renderer — catches CEPI source_url + any http(s) value
        const URL_KEYS = new Set(['source_url', 'url', 'link', 'doc_url', 'dashboard_url',
                                  'permalink', 'profile_url', 'evidence_url']);
        const isString = typeof v === 'string';
        if (v != null && isString && (URL_KEYS.has(key) || /^https?:\/\//i.test(v))) {
            return (
                <span className="three_dots_text">
                    <a className="obs_link" href={v} target="_blank" rel="noopener noreferrer">
                        {v.length > 80 ? v.slice(0, 77) + '…' : v}
                    </a>
                </span>
            );
        }

        // Helper: render a primitive value, auto-linkifying http(s) strings.
        const renderPrimitive = (val) => {
            if (val == null) return '-';
            if (typeof val === 'string' && /^https?:\/\//i.test(val)) {
                const text = val.length > 80 ? val.slice(0, 77) + '…' : val;
                return (
                    <a className="obs_link" href={val} target="_blank" rel="noopener noreferrer">
                        {text}
                    </a>
                );
            }
            return String(val);
        };

        if (Array.isArray(v)) {
            if (v.every(el => typeof el !== 'object' || el === null)) {
                // Primitive array — interleave commas + linkify each element
                return v.map((el, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && ', '}
                        {renderPrimitive(el)}
                    </React.Fragment>
                ));
            }
            // Array of objects — render each object's k/v pairs, linkifying URLs
            return v.map((el, index) => (
                <div key={index} className="three_dots_text" style={{ marginBottom: index < v.length - 1 ? 4 : 0 }}>
                    {el && typeof el === 'object'
                        ? Object.entries(el).map(([k, val], ki, arr) => (
                            <React.Fragment key={k}>
                                {ki > 0 && ' · '}
                                <span className="obs_key">{k}:</span>{' '}
                                {renderPrimitive(val)}
                            </React.Fragment>
                        ))
                        : renderPrimitive(el)
                    }
                </div>
            ));
        }

        if (v && typeof v === 'object') {
            return Object.entries(v).map(([k, val], i, arr) => (
                <React.Fragment key={k}>
                    {i > 0 && ' · '}
                    <span className="obs_key">{k}:</span> {renderPrimitive(val)}
                </React.Fragment>
            ));
        }

        return renderPrimitive(v);
    }

    // Prepare rows for CommonTable with rowSpan support
    const rows = sortedEvidenceData.map((record, rowIndex) => {
        const timelineId = getTimelineId(record);
        const rowKey = getRowKey(record);
        const isPending = record?.exception_status === 'PENDING';
        const selectable = isSelectable(record);

        // Checkbox column
        const checkboxCell = (
            <input
                type="checkbox"
                checked={selectedKeys.includes(rowKey)}
                onChange={() => handleSelectRow(rowKey)}
                disabled={!selectable}
                title={isPending
                    ? 'An exception is already pending for this row'
                    : (!timelineId ? 'This row has no reference to raise an exception against' : undefined)}
                style={{ cursor: selectable ? 'pointer' : 'not-allowed', width: 16, height: 16 }}
            />
        );
        // Checkbox column (only for DWI)
        const rowData = isDWI ? [checkboxCell] : [];

        orderedKeys.forEach((key, colIndex) => {
            const v = record[key];

            // Handle suites_name column with rowSpan
            if (key === 'suites_name' && suitesNameIndex !== -1) {
                const suiteName = v || '-'
                const group = suitesNameGroups[suiteName]

                // If this is the first row of the group, add rowSpan
                if (group && rowIndex === group.startIndex) {
                    rowData.push({
                        value: getCellValue(key, v, rowIndex),
                        rowSpan: group.count,
                        style: { borderRight: '1px solid #334155' }
                    });
                    return;
                }
                // If this is not the first row, skip this cell
                if (group && rowIndex > group.startIndex && rowIndex < group.startIndex + group.count) {
                    rowData.push({ skip: true });
                    return;
                }
            }

            rowData.push(getCellValue(key, v, rowIndex));
        });

        // Add Raise Exception button column (only for non-DWI)
        if (!isDWI) {
            rowData.push(
                <span
                    key="raise-exception"
                    onClick={() => {
                        if (timelineId && !isPending) {
                            setSelectedEvidenceTimelineId(timelineId);
                            setShowRaiseExceptionModal(true);
                        }
                    }}
                    className={'blue_text cursor_pointer'}
                    style={isPending ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                    title={isPending ? 'Exception request is pending. You cannot create a new exception at this time.' : ''}
                >
                    {isPending ? 'Exception Pending' : 'Raise Exception'}
                </span>
            );
        }

        return {
            data: rowData
        };
    })

    // Header actions move into CommonModal's footer `buttons` — it renders a
    // plain text title, and this is where the rest of the app puts modal
    // actions. The takedown tooltip is dropped: the confirmation modal it
    // opens already spells out the same SLA detail in its body.
    const observationActions = [
        ...(canTakedown ? [{
            text: 'Initiate Takedown',
            variant: 'error',
            onClick: () => { setTakedownResult(null); setShowTakedownConfirm(true); },
        }] : []),
        ...(isDWI && selectedKeys.length > 0 ? [{
            text: `Raise Exception for ${selectedKeys.length} Selected`,
            variant: 'primary',
            onClick: () => { setSelectedEvidenceTimelineId(null); setShowRaiseExceptionModal(true); },
        }] : []),
    ];

    const takedownActions = takedownResult
        ? [{ text: 'Close', variant: 'primary', onClick: () => setShowTakedownConfirm(false) }]
        : [
            { text: 'Cancel', variant: 'secondary', onClick: () => setShowTakedownConfirm(false), disabled: takedownBusy },
            {
                text: 'Confirm — NMT will initiate takedown',
                variant: 'error',
                onClick: handleConfirmTakedown,
                loading: takedownBusy,
                loadingText: 'Submitting…',
                disabled: takedownBusy || !targetDomain,
            },
        ];

    return (
        <CommonModal
            show={props?.show}
            onHide={props?.onHide}
            title="Observations"
            size="xl"
            buttons={observationActions}
        >
            {isLoading ? (
                <div className="mb-5">
                    <ScreenLoader />
                </div>
            ) : rows && rows.length > 0 ? (
                <CommonTable headers={headers} rows={rows} />
            ) : (
                <div className="text-center my-5">No Issue Found </div>
            )}

                {showRaiseExceptionModal && (
                    <RaiseExceptionModal
                        show={showRaiseExceptionModal}
                        onHide={() => {
                            setShowRaiseExceptionModal(false);
                            setSelectedEvidenceTimelineId(null);
                        }}
                        evidenceTimelineId={selectedEvidenceTimelineId}
                        evidenceTimelineIds={selectedEvidenceTimelineId ? [] : selectedTimelineIds}
                        isDWI={isDWI}
                        onSuccess={() => {
                            setShowRaiseExceptionModal(false);
                            // Update local data to reflect pending exception
                            // Mark by row, not by timeline id: ids can be shared
                            // across rows, so marking by id would flip rows the
                            // user never selected.
                            setEvidenceData(prev => prev.map(item => {
                                const hit = selectedEvidenceTimelineId
                                    ? getTimelineId(item) === selectedEvidenceTimelineId
                                    : selectedKeys.includes(item?.__rowKey);
                                return hit ? { ...item, exception_status: 'PENDING' } : item;
                            }));
                            setSelectedEvidenceTimelineId(null);
                            setSelectedKeys([]);
                            props.onRefresh?.();
                        }}
                    />
                )}

            {showTakedownConfirm && (
                <CommonModal
                    show={showTakedownConfirm}
                    // Ignore dismissal while the request is in flight — this
                    // replaces the old backdrop="static", which CommonModal
                    // does not expose.
                    onHide={() => { if (!takedownBusy) setShowTakedownConfirm(false); }}
                    title="Confirm Managed Takedown"
                    buttons={takedownActions}
                >
                    {!takedownResult && (
                        <>
                            <p className="mb-3">
                                NMT will operate the takedown on your behalf after you confirm.
                                The target will be reported to the relevant registrar, hosting
                                provider, or platform.
                            </p>
                            <div className="obs_takedown_summary mb-3">
                                <div className="mb-2"><b>Target:</b> {targetDomain || '(not detected — review the finding URL before confirming)'}</div>
                                <div className="mb-2"><b>Category:</b> {category}</div>
                                <div className="mb-2"><b>Finding:</b> {props?.evidencelist?.issue_name || props?.evidencelist?.description || '—'}</div>
                            </div>
                            <p className="obs_takedown_note mb-3">
                                <b>SLA:</b> Initiate within 24 hours of your confirmation. Target full
                                resolution within 5 calendar days. Progress is tracked on the
                                Takedown SLA widget on your dashboard.
                            </p>
                        </>
                    )}
                    {takedownResult && (
                        <div className={takedownResult.ok ? 'text-success' : 'text-danger'}>
                            <p>{takedownResult.message}</p>
                            {takedownResult.ok && takedownResult.sla_initiate && (
                                <p className="obs_takedown_note">
                                    Initiate target: {new Date(takedownResult.sla_initiate).toLocaleString()}<br />
                                    Resolve target:  {new Date(takedownResult.sla_resolve).toLocaleString()}
                                </p>
                            )}
                        </div>
                    )}
                </CommonModal>
            )}
        </CommonModal>
    )
}

export default IssueCountsModal
